import { PushPin } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { usePins, useTogglePin } from "~/api/queries";
import { Tooltip } from "~/foundation/components/Tooltip";
import { cn } from "~/foundation/utils/cn";

/**
 * W10 §3 — pin this document to the top of its collection.
 *
 * Unlike the star beside it, a pin is the WORKSPACE's, so this is gated on being able to change
 * the collection. The server gates on nothing but the session — recorded as a backend gap — and
 * a client that mirrored that would let any reader rearrange everyone's landing row.
 *
 * A document with no collection has nowhere to be pinned (the home pins are a separate scope the
 * platform keys as `collectionId IS NULL`, and slate's home is its own surface), so the control
 * simply is not there rather than failing on click.
 */
export function PinButton({
  documentId,
  collectionId,
}: {
  documentId: string;
  collectionId: string;
}) {
  const { t } = useTranslation();
  const { data } = usePins(collectionId);
  const toggle = useTogglePin(collectionId);
  const pinned = (data?.pins ?? []).some((pin) => pin.documentId === documentId);
  const label = pinned ? t("Unpin from collection") : t("Pin to collection");

  return (
    <Tooltip content={label}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={pinned}
        disabled={toggle.isPending}
        onClick={() =>
          toggle.mutate(documentId, {
            onSuccess: (result) =>
              toast.success(result.pinned ? t("Pinned to the collection") : t("Unpinned")),
          })
        }
        className={cn(
          "inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm",
          "transition-colors duration-100 ease-in-out motion-reduce:transition-none",
          "hover:bg-hover-bg disabled:pointer-events-none disabled:opacity-50",
          pinned ? "text-accent" : "text-text-secondary hover:text-text",
        )}
      >
        <PushPin size={18} weight={pinned ? "fill" : "regular"} aria-hidden />
      </button>
    </Tooltip>
  );
}
